// @flow strict
import React, { useEffect, useRef, useState, useMemo } from "react";
import {
  FaPhoneAlt,
  FaWhatsapp,
  FaEnvelope,
  FaUser,
  FaFilter,
  FaCalendarAlt,
  FaUserPlus,
  FaUserSlash,
  FaUserCheck,
  FaSearch,
  FaTimes,
  FaUsers,
} from "react-icons/fa";
import { FiDownload, FiUpload, FiRefreshCw, FiX } from "react-icons/fi";
import {
  BsChevronDown,
  BsChevronUp,
  BsChevronLeft,
  BsChevronRight,
  BsThreeDotsVertical,
} from "react-icons/bs";
import { Link } from "react-router-dom";
import {
  exportToCSV,
  exportToExcel,
  formatDateForExport,
} from "../../functions/exportsFunctions";
import BlockUserModal from "../../components/admin/BlockUserModal";
import ModalAddMember from "../../components/admin/ModalAddMember";
import { useMemberStore } from "../../store/memberStore";

function formatFrenchDateCapitalized(isoDate) {
  const date = new Date(isoDate);
  const formatted = new Intl.DateTimeFormat("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
}

// Composant Card pour mobile/tablette
function TeamMemberCard({ member, onToggleStatus }) {
  const initials =
    `${member.prenom?.charAt(0) || ""}${member.nom?.charAt(0) || ""}`.toUpperCase();
  const fullName = `${member.prenom || ""} ${member.nom || ""}`.trim();
  const displayName =
    fullName.length > 20 ? fullName.substring(0, 20) + "..." : fullName;

  return (
    <div className="bg-white border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-200">
      {/* En-tête de la carte */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            {/* Avatar avec initiales */}
            <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-primary to-secondary-200 text-white font-semibold text-lg">
              {initials || <FaUser size={20} />}
            </div>
            <div className="min-w-0">
              <h3
                className="font-poppins font-semibold text-title truncate"
                title={fullName}
              >
                {displayName}
              </h3>
              {member.email && (
                <a
                  href={`mailto:${member.email}`}
                  className="font-nunito text-sm text-text hover:text-secondary-600 truncate block"
                  title={member.email}
                >
                  {member.email}
                </a>
              )}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <span
              className={`px-2 py-1 text-xs font-semibold ${
                member.is_active
                  ? "bg-green-100 text-green-800"
                  : "bg-red-100 text-red-800"
              }`}
            >
              {member.is_active ? "Actif" : "Désactivé"}
            </span>
            <span className="px-2 py-1 text-xs font-medium bg-slate-100 text-slate-800">
              {member.role?.name || ""}
            </span>
          </div>
        </div>
      </div>

      {/* Informations de contact */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="font-nunito font-medium text-sm">
              {member.telephone || "Non renseigné"}
            </span>
          </div>
          <div className="flex gap-2">
            {member.telephone && (
              <a
                href={`tel:${member.telephone.replace(/\s+/g, "")}`}
                title="Composer le numéro"
                className="p-2 bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
              >
                <FaPhoneAlt size={14} />
              </a>
            )}
            {member.whatsapp && (
              <a
                href={`https://wa.me/${member.whatsapp.replace(/\s+/g, "")}`}
                target="_blank"
                rel="noopener noreferrer"
                title="WhatsApp"
                className="p-2 bg-green-50 text-green-600 hover:bg-green-100 transition-colors"
              >
                <FaWhatsapp size={14} />
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Date de création */}
      <div className="px-4 py-2 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <FaCalendarAlt size={12} className="text-gray-400" />
          <span className="font-nunito text-xs text-gray-600">
            {formatFrenchDateCapitalized(member.created_at)}
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className="p-3 bg-gray-50">
        {member.role?.id !== 1 && (
          <button
            onClick={() => onToggleStatus(member)}
            className={`w-full flex items-center justify-center gap-2 px-4 py-2 font-medium transition-colors ${
              member.is_active
                ? "bg-red-50 text-red-700 hover:bg-red-100 border border-red-200"
                : "bg-green-50 text-green-700 hover:bg-green-100 border border-green-200"
            }`}
          >
            {member.is_active ? (
              <>
                <FaUserSlash size={14} />
                Bloquer
              </>
            ) : (
              <>
                <FaUserCheck size={14} />
                Débloquer
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

function TeamsPage() {
  const [showViewDropdown, setShowViewDropdown] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showDateFilters, setShowDateFilters] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: "",
    dateCreation: "",
    search: "",
  });
  const [exportLoading, setExportLoading] = useState(false);

  // États pour la pagination et modals
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [blockUserModal, setBlockUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const searchInputRef = useRef(null);
  const dropdownRef = useRef(null);
  const filtersDropdownRef = useRef(null);
  const dateFiltersDropdownRef = useRef(null);

  const {
    members: admins,
    getMembers: getAdmins,
    isLoadingMember: isLoadingAdmins,
    syncMemberStatusChange: syncAdminStatusChange,
    syncAddNewMember: syncAddNewAdmin,
  } = useMemberStore();

  useEffect(() => {
    getAdmins();
  }, []);

  // Fermer les dropdowns en cliquant à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowViewDropdown(false);
      }
      if (
        filtersDropdownRef.current &&
        !filtersDropdownRef.current.contains(event.target)
      ) {
        setShowFilters(false);
      }
      if (
        dateFiltersDropdownRef.current &&
        !dateFiltersDropdownRef.current.contains(event.target)
      ) {
        setShowDateFilters(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleToggleStatus = (member) => {
    setSelectedUser(member);
    setBlockUserModal(true);
  };

  // Gestion des filtres
  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }));
    setCurrentPage(1);

    if (filterName === "dateCreation") {
      setShowDateFilters(false);
    }
    if (filterName === "status") {
      setShowFilters(false);
    }
  };

  const getFilterDateName = (value) => {
    switch (value) {
      case "today":
        return "Aujourd'hui";
      case "week":
        return "Cette semaine";
      case "month":
        return "Ce mois";
      case "year":
        return "Cette année";
      default:
        return "Toutes les dates";
    }
  };

  const getFilterStatusName = (value) => {
    switch (value) {
      case "actif":
        return "Actifs";
      case "inactif":
        return "Inactifs";
      default:
        return "Tous les statuts";
    }
  };

  const resetFilters = () => {
    setFilters({
      status: "",
      dateCreation: "",
      search: "",
    });
    setShowFilters(false);
    setShowDateFilters(false);
    setShowMobileFilters(false);
    setCurrentPage(1);
  };

  // Filtrer les admins
  const filteredAdmins = useMemo(() => {
    return admins.filter((admin) => {
      const searchMatch =
        !filters.search ||
        admin.nom?.toLowerCase().includes(filters.search.toLowerCase()) ||
        admin.prenom?.toLowerCase().includes(filters.search.toLowerCase()) ||
        admin.email?.toLowerCase().includes(filters.search.toLowerCase()) ||
        admin.telephone?.includes(filters.search);

      const statusMatch =
        !filters.status ||
        (filters.status === "actif" && admin.is_active) ||
        (filters.status === "inactif" && !admin.is_active);

      const dateMatch =
        !filters.dateCreation ||
        checkDateCreation(admin.created_at, filters.dateCreation);

      return searchMatch && statusMatch && dateMatch;
    });
  }, [admins, filters]);

  function checkDateCreation(createdAt, filterType) {
    const createdDate = new Date(createdAt);
    const today = new Date();

    switch (filterType) {
      case "today":
        return createdDate.toDateString() === today.toDateString();
      case "week":
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        return createdDate >= startOfWeek;
      case "month":
        return (
          createdDate.getMonth() === today.getMonth() &&
          createdDate.getFullYear() === today.getFullYear()
        );
      default:
        return true;
    }
  }

  // Gestionnaire d'export
  const handleExport = (type) => {
    const dataToExport = filteredAdmins;

    if (dataToExport.length === 0) {
      alert("Aucune donnée à exporter");
      return;
    }

    const timestamp = new Date().toISOString().split("T")[0];
    const baseFilename = `equipe_${timestamp}`;

    try {
      setExportLoading(true);
      const headers = [
        "Nom",
        "Prénom",
        "Téléphone",
        "Email",
        "Rôle",
        "Statut",
        "Date de création",
      ];
      const data = dataToExport.map((admin) => [
        admin.nom || "",
        admin.prenom || "",
        admin.telephone || "",
        admin.email || "",
        admin.role?.name || "",
        admin.is_active ? "Actif" : "Inactif",
        formatDateForExport(admin.created_at),
      ]);

      if (type === "csv") {
        exportToCSV(headers, data, `${baseFilename}.csv`);
      } else if (type === "excel") {
        exportToExcel(headers, data, `${baseFilename}.xlsx`);
      }
    } catch (error) {
      console.error("Erreur lors de l'exportation:", error);
    } finally {
      setExportLoading(false);
      setShowViewDropdown(false);
    }
  };

  // Pagination
  const totalPages = Math.ceil(filteredAdmins.length / itemsPerPage);
  const paginatedAdmins = filteredAdmins.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  // Colonnes pour le tableau desktop
  const columns = [
    {
      headerName: "Membre",
      width: "25%",
      render: (admin) => {
        const initials =
          `${admin.prenom?.charAt(0) || ""}${admin.nom?.charAt(0) || ""}`.toUpperCase();
        const fullName = `${admin.prenom || ""} ${admin.nom || ""}`.trim();

        return (
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-br from-primary to-secondary-200 text-white font-semibold">
              {initials || <FaUser size={16} />}
            </div>
            <div className="min-w-0">
              <p className="font-poppins font-semibold text-title truncate">
                {fullName}
              </p>
              {admin.email && (
                <a
                  href={`mailto:${admin.email}`}
                  className="font-nunito text-sm text-text hover:text-secondary-600 truncate block"
                  title={admin.email}
                >
                  {admin.email}
                </a>
              )}
            </div>
          </div>
        );
      },
    },
    {
      headerName: "Contact",
      width: "20%",
      render: (admin) => (
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="font-nunito text-sm">
              {admin.telephone || "Non renseigné"}
            </span>
            {admin.telephone && (
              <div className="flex gap-1">
                <a
                  href={`tel:${admin.telephone.replace(/\s+/g, "")}`}
                  title="Appeler"
                  className="p-1 text-blue-600 hover:text-blue-800"
                >
                  <FaPhoneAlt size={12} />
                </a>
                <a
                  href={`https://wa.me/${admin.whatsapp?.replace(/\s+/g, "") || admin.telephone.replace(/\s+/g, "")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="WhatsApp"
                  className="p-1 text-green-600 hover:text-green-800"
                >
                  <FaWhatsapp size={12} />
                </a>
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      headerName: "Rôle & Statut",
      width: "20%",
      render: (admin) => (
        <div className="flex flex-col gap-2">
          <span className="px-2 py-1 text-xs font-medium bg-slate-100 text-slate-800 w-fit">
            {admin.role?.name || ""}
          </span>
          <span
            className={`px-2 py-1 text-xs font-semibold w-fit ${
              admin.is_active
                ? "bg-green-100 text-green-800"
                : "bg-red-100 text-red-800"
            }`}
          >
            {admin.is_active ? "Actif" : "Désactivé"}
          </span>
        </div>
      ),
    },
    {
      headerName: "Date création",
      width: "15%",
      render: (admin) => (
        <div className="font-nunito text-sm text-text">
          {new Date(admin.created_at).toLocaleDateString("fr-FR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          })}
        </div>
      ),
    },
    {
      headerName: "Actions",
      width: "20%",
      render: (admin) =>
        admin.role?.id !== 1 ? (
          <div className="flex gap-2">
            <button
              onClick={() => handleToggleStatus(admin)}
              className={`flex items-center gap-2 px-4 py-2 font-medium transition-colors ${
                admin.is_active
                  ? "bg-red-50 text-red-700 hover:bg-red-100 border border-red-200"
                  : "bg-green-50 text-green-700 hover:bg-green-100 border border-green-200"
              }`}
            >
              {admin.is_active ? (
                <>
                  <FaUserSlash size={14} />
                  Bloquer
                </>
              ) : (
                <>
                  <FaUserCheck size={14} />
                  Débloquer
                </>
              )}
            </button>
          </div>
        ) : (
          <span className="text-gray-400 text-sm">Super Admin</span>
        ),
    },
  ];

  return (
    <div className="md:p-6">
      {/* Modals */}
      <BlockUserModal
        isBlocked={!selectedUser?.is_active}
        isOpen={blockUserModal}
        onClose={() => setBlockUserModal(false)}
        member={selectedUser}
        onRequest={() =>
          syncAdminStatusChange(selectedUser?.id, !selectedUser?.is_active)
        }
      />

      <ModalAddMember
        open={openAddModal}
        handleClose={() => setOpenAddModal(false)}
        syncNewMember={syncAddNewAdmin}
      />

      {/* En-tête principal */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6 gap-4">
        <div className="flex-1">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
            <h2 className="font-poppins font-semibold text-[18px] text-title">
              Équipe
            </h2>
            <span className="bg-secondary-50 text-secondary-700 px-3 py-1 font-nunito text-[14px]">
              {admins.length} membre{admins.length !== 1 ? "s" : ""}
            </span>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-1">
            <p className="font-nunito text-[14px] text-text">
              Gérez les membres de votre équipe, leurs rôles et leurs accès.
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
          {/* Dropdown Options */}
          <div className="relative select-none" ref={dropdownRef}>
            <button
              className="flex items-center justify-between gap-2 py-2 px-4 border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
              onClick={() => setShowViewDropdown(!showViewDropdown)}
              disabled={exportLoading}
            >
              <span className="font-nunito font-semibold text-[14px]">
                {exportLoading ? "Export..." : "Options"}
              </span>
              {exportLoading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-secondary-600"></div>
              ) : showViewDropdown ? (
                <BsChevronUp size={12} />
              ) : (
                <BsChevronDown size={12} />
              )}
            </button>

            {showViewDropdown && (
              <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 shadow-lg z-20 w-64 overflow-hidden">
                <div className="p-2">
                  <div className="px-3 py-2 font-poppins font-medium text-sm text-subtitle uppercase tracking-wide border-b">
                    Export
                  </div>
                  <button
                    className="w-full text-left py-3 px-3 font-nunito text-sm text-text hover:bg-gray-50 flex items-center gap-3"
                    onClick={() => handleExport("excel")}
                  >
                    <FiDownload size={16} className="text-green-600" />
                    <span>Exporter en Excel</span>
                  </button>
                  <button
                    className="w-full text-left py-3 px-3 font-nunito text-sm text-text hover:bg-gray-50 flex items-center gap-3"
                    onClick={() => handleExport("csv")}
                  >
                    <FiDownload size={16} className="text-blue-600" />
                    <span>Exporter en CSV</span>
                  </button>

                  <div className="border-t my-2"></div>

                  {/* <div className="px-3 py-2 font-poppins font-medium text-sm text-subtitle uppercase tracking-wide border-b">
                    Autres
                  </div>
                  <button
                    className="w-full text-left py-3 px-3 font-nunito text-sm text-text hover:bg-gray-50 flex items-center gap-3"
                    onClick={() => {
                      console.log("Importer des membres");
                      setShowViewDropdown(false);
                    }}
                  >
                    <FiUpload size={16} className="text-gray-600" />
                    <span>Importer des membres</span>
                  </button>
                  <button
                    className="w-full text-left py-3 px-3 font-nunito text-sm text-text hover:bg-gray-50 flex items-center gap-3"
                    onClick={() => {
                      console.log("Fusionner des membres");
                      setShowViewDropdown(false);
                    }}
                  >
                    <FiRefreshCw size={16} className="text-gray-600" />
                    <span>Fusionner des membres</span>
                  </button> */}
                </div>
              </div>
            )}
          </div>

          {/* Bouton Ajouter */}
          <button
            className="flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-primary to-secondary-700 text-white hover:from-secondary-600 hover:to-secondary-800 transition-all shadow-sm"
            onClick={() => setOpenAddModal(true)}
          >
            <FaUserPlus size={16} />
            <span className="font-nunito font-semibold text-[14px]">
              Ajouter un membre
            </span>
          </button>
        </div>
      </div>

      {/* Barre de recherche et filtres */}
      <div className="bg-gradient-to-r from-secondary-50 to-purple-50 p-4 md:p-6 mb-6 border border-secondary-100">
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center">
          {/* Barre de recherche */}
          <div className="flex-1 relative min-w-0">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaSearch size={18} className="text-secondary-400" />
              </div>

              <input
                ref={searchInputRef}
                type="text"
                placeholder="Rechercher par nom, email ou téléphone..."
                value={filters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
                className="w-full pl-12 pr-10 py-3 border border-gray-300 font-nunito text-[14px] text-text focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent"
              />

              {filters.search && (
                <button
                  className="absolute inset-y-0 right-0 pr-3 flex items-center hover:bg-gray-100 px-2"
                  onClick={() => handleFilterChange("search", "")}
                >
                  <FaTimes size={16} className="text-gray-400" />
                </button>
              )}
            </div>
          </div>

          {/* Boutons de filtre */}
          <div className="flex flex-wrap gap-3 w-full lg:w-auto">
            {/* Filtre Statut */}
            <div className="relative" ref={filtersDropdownRef}>
              <button
                className="flex items-center justify-center gap-2 px-4 py-3 bg-white border border-gray-300 text-gray-700 font-nunito font-semibold text-[14px] hover:bg-gray-50 transition-colors"
                onClick={() => setShowFilters(!showFilters)}
              >
                <FaFilter size={14} />
                {getFilterStatusName(filters.status)}
                {showFilters ? (
                  <BsChevronUp size={14} />
                ) : (
                  <BsChevronDown size={14} />
                )}
              </button>

              {showFilters && (
                <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 shadow-lg z-20 w-48 overflow-hidden">
                  <button
                    className="w-full text-left py-3 px-4 font-nunito text-sm text-text hover:bg-gray-50"
                    onClick={() => handleFilterChange("status", "")}
                  >
                    Tous les statuts
                  </button>
                  <button
                    className="w-full text-left py-3 px-4 font-nunito text-sm text-text hover:bg-gray-50"
                    onClick={() => handleFilterChange("status", "actif")}
                  >
                    Actifs
                  </button>
                  <button
                    className="w-full text-left py-3 px-4 font-nunito text-sm text-text hover:bg-gray-50"
                    onClick={() => handleFilterChange("status", "inactif")}
                  >
                    Inactifs
                  </button>
                </div>
              )}
            </div>

            {/* Filtre Date */}
            <div className="relative" ref={dateFiltersDropdownRef}>
              <button
                className="flex items-center justify-center gap-2 px-4 py-3 bg-white border border-gray-300 text-gray-700 font-nunito font-semibold text-[14px] hover:bg-gray-50 transition-colors"
                onClick={() => setShowDateFilters(!showDateFilters)}
              >
                <FaCalendarAlt size={14} />
                {getFilterDateName(filters.dateCreation)}
                {showDateFilters ? (
                  <BsChevronUp size={14} />
                ) : (
                  <BsChevronDown size={14} />
                )}
              </button>

              {showDateFilters && (
                <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 shadow-lg z-20 w-48 overflow-hidden">
                  <button
                    className="w-full text-left py-3 px-4 font-nunito text-sm text-text hover:bg-gray-50"
                    onClick={() => handleFilterChange("dateCreation", "")}
                  >
                    Toutes les dates
                  </button>
                  <button
                    className="w-full text-left py-3 px-4 font-nunito text-sm text-text hover:bg-gray-50"
                    onClick={() => handleFilterChange("dateCreation", "today")}
                  >
                    Aujourd'hui
                  </button>
                  <button
                    className="w-full text-left py-3 px-4 font-nunito text-sm text-text hover:bg-gray-50"
                    onClick={() => handleFilterChange("dateCreation", "week")}
                  >
                    Cette semaine
                  </button>
                  <button
                    className="w-full text-left py-3 px-4 font-nunito text-sm text-text hover:bg-gray-50"
                    onClick={() => handleFilterChange("dateCreation", "month")}
                  >
                    Ce mois
                  </button>
                </div>
              )}
            </div>

            {/* Bouton Filtres Mobile */}
            <button
              className="flex lg:hidden items-center justify-center gap-2 px-4 py-3 bg-white border border-gray-300 text-gray-700 font-nunito font-semibold text-[14px] hover:bg-gray-50 transition-colors"
              onClick={() => setShowMobileFilters(true)}
            >
              <FaFilter size={16} />
              Plus de filtres
            </button>

            {/* Bouton Réinitialiser */}
            {(filters.status || filters.dateCreation || filters.search) && (
              <button
                onClick={resetFilters}
                className="flex items-center gap-2 px-4 py-3 font-nunito text-[14px] font-semibold text-gray-600 border border-gray-300 hover:bg-gray-50"
              >
                <FiX size={16} />
                Réinitialiser
              </button>
            )}
          </div>
        </div>

        {/* Indicateur de filtres actifs */}
        {(filters.status || filters.dateCreation || filters.search) && (
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="font-nunito text-sm text-gray-600">
              Filtres actifs:
            </span>
            {filters.status && (
              <span className="bg-secondary-100 text-secondary-800 px-3 py-1 text-xs font-medium">
                Statut: {getFilterStatusName(filters.status)}
              </span>
            )}
            {filters.dateCreation && (
              <span className="bg-purple-100 text-purple-800 px-3 py-1 text-xs font-medium">
                Date: {getFilterDateName(filters.dateCreation)}
              </span>
            )}
            {filters.search && (
              <span className="bg-green-100 text-green-800 px-3 py-1 text-xs font-medium">
                Recherche: "{filters.search}"
              </span>
            )}
            <span className="bg-gray-100 text-gray-800 px-3 py-1 text-xs font-medium">
              {filteredAdmins.length} résultat
              {filteredAdmins.length !== 1 ? "s" : ""}
            </span>
          </div>
        )}
      </div>

      {/* Pagination en haut */}
      {totalPages > 1 && filteredAdmins.length > 0 && (
        <div className="mb-4 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white p-4 border border-gray-200">
          <div className="font-nunito text-sm text-text">
            Page {currentPage} sur {totalPages} • {filteredAdmins.length} membre
            {filteredAdmins.length > 1 ? "s" : ""}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="p-2 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              <BsChevronLeft size={18} />
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }

              return (
                <button
                  key={i}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`w-8 h-8 text-xs font-nunito ${
                    currentPage === pageNum
                      ? "bg-secondary-600 text-white"
                      : "border border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              className="p-2 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              <BsChevronRight size={18} />
            </button>
          </div>
        </div>
      )}

      {isLoadingAdmins ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary-600"></div>
        </div>
      ) : (
        <>
          {/* Tableau Desktop */}
          <div className="hidden lg:block bg-white border border-gray-200 overflow-hidden mb-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                  <tr>
                    {columns.map((column, index) => (
                      <th
                        key={index}
                        className="px-6 py-4 text-left font-poppins font-bold text-sm text-title border-b border-gray-200"
                        style={{ width: column.width }}
                      >
                        {column.headerName}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {paginatedAdmins.map((admin, index) => (
                    <tr
                      key={admin.id}
                      className={
                        index % 2 === 0
                          ? "bg-white"
                          : "bg-gray-50 hover:bg-gray-100"
                      }
                    >
                      {columns.map((column, colIndex) => (
                        <td key={colIndex} className="px-6 py-4">
                          <div className="flex items-center h-full">
                            {column.render(admin)}
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Cartes Mobile/Tablette */}
          <div className="lg:hidden space-y-4 mb-6">
            {paginatedAdmins.map((member) => (
              <TeamMemberCard
                key={member.id}
                member={member}
                onToggleStatus={handleToggleStatus}
              />
            ))}
          </div>

          {/* Pagination en bas */}
          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white p-4 border border-gray-200">
              <div className="font-nunito text-sm text-text">
                Page {currentPage} sur {totalPages} • {filteredAdmins.length}{" "}
                membre
                {filteredAdmins.length > 1 ? "s" : ""}
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                  className="p-2 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  <BsChevronLeft size={18} />
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={i}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`w-8 h-8 text-xs font-nunito ${
                        currentPage === pageNum
                          ? "bg-secondary-600 text-white"
                          : "border border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                  className="p-2 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  <BsChevronRight size={18} />
                </button>
              </div>
            </div>
          )}

          {/* Message vide */}
          {filteredAdmins.length === 0 && (
            <div className="text-center py-12 bg-white border border-gray-200">
              <FaUsers className="mx-auto text-gray-300" size={48} />
              <p className="font-nunito text-sm text-text mt-3">
                {filters.search || filters.status || filters.dateCreation
                  ? "Aucun membre ne correspond à vos critères"
                  : "Aucun membre dans l'équipe"}
              </p>
              {filters.search || filters.status || filters.dateCreation ? (
                <button
                  onClick={resetFilters}
                  className="mt-4 px-4 py-2 bg-secondary-600 text-white font-nunito text-sm hover:bg-secondary-700"
                >
                  Réinitialiser les filtres
                </button>
              ) : (
                <button
                  onClick={() => setOpenAddModal(true)}
                  className="mt-4 px-4 py-2 bg-secondary-600 text-white font-nunito text-sm hover:bg-secondary-700"
                >
                  Ajouter un membre
                </button>
              )}
            </div>
          )}
        </>
      )}

      {/* Modal des filtres pour mobile */}
      {showMobileFilters && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            onClick={() => setShowMobileFilters(false)}
          />
          <div className="fixed top-0 left-0 h-full w-4/5 max-w-sm bg-white z-50 shadow-xl transform transition-transform lg:hidden overflow-hidden">
            <div className="p-4 h-full overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="font-poppins font-semibold text-[18px] text-title">
                  Filtres
                </h2>
                <button
                  onClick={() => setShowMobileFilters(false)}
                  className="text-gray-500 hover:text-gray-700 p-1"
                >
                  <FiX size={24} />
                </button>
              </div>

              <div className="space-y-6">
                {/* Filtre Statut */}
                <div>
                  <label className="font-poppins font-medium text-[15px] text-subtitle block mb-2">
                    Statut
                  </label>
                  <select
                    value={filters.status}
                    onChange={(e) =>
                      handleFilterChange("status", e.target.value)
                    }
                    className="w-full border border-gray-300 px-3 py-2 font-nunito text-[14px] text-text focus:outline-none focus:ring-2 focus:ring-secondary-500"
                  >
                    <option value="">Tous les statuts</option>
                    <option value="actif">Actif</option>
                    <option value="inactif">Inactif</option>
                  </select>
                </div>

                {/* Filtre Date de création */}
                <div>
                  <label className="font-poppins font-medium text-[15px] text-subtitle block mb-2">
                    Date de création
                  </label>
                  <select
                    value={filters.dateCreation}
                    onChange={(e) =>
                      handleFilterChange("dateCreation", e.target.value)
                    }
                    className="w-full border border-gray-300 px-3 py-2 font-nunito text-[14px] text-text focus:outline-none focus:ring-2 focus:ring-secondary-500"
                  >
                    <option value="">Toutes les dates</option>
                    <option value="today">Aujourd'hui</option>
                    <option value="week">Cette semaine</option>
                    <option value="month">Ce mois</option>
                  </select>
                </div>
              </div>

              {/* Boutons d'action */}
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200">
                <div className="flex gap-3">
                  <button
                    onClick={resetFilters}
                    className="flex-1 px-4 py-3 font-nunito text-[14px] text-text border border-gray-300 hover:bg-gray-50"
                  >
                    Réinitialiser
                  </button>
                  <button
                    onClick={() => setShowMobileFilters(false)}
                    className="flex-1 px-4 py-3 bg-secondary-600 text-white font-nunito text-[14px] hover:bg-secondary-700"
                  >
                    Appliquer
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default TeamsPage;
